import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./Home.css";

const Home = ({ runIntroAnimation, onIntroAnimationComplete }) => {
  // animationStage is for internal CSS class, not core logic here.
  const [animationStage, setAnimationStage] = useState("start");
  const [elementsVisible, setElementsVisible] = useState({
    logo: false,
    text: false,
    bubbleLeft: false,
    bubbleRight: false,
    bubbleBottom: false,
  });

  const introCompletedRef = useRef(false);

  useEffect(() => {
    // Disable scrolling when splash screen is active
    document.body.classList.add("no-scroll");

    if (runIntroAnimation === true) {
      setAnimationStage("start"); // Ensure CSS class is set for animation start
      // Ensure all elements are explicitly hidden before starting timeouts
      setElementsVisible({
        logo: false,
        text: false,
        bubbleLeft: false,
        bubbleRight: false,
        bubbleBottom: false,
      });
      introCompletedRef.current = false;

      // Sequence of animations - Reduced initial delays
      setTimeout(
        () => setElementsVisible((prev) => ({ ...prev, logo: true })),
        200
      ); // Was 500ms
      setTimeout(
        () => setElementsVisible((prev) => ({ ...prev, text: true })),
        700
      ); // Was 1500ms
      setTimeout(
        () => setElementsVisible((prev) => ({ ...prev, bubbleLeft: true })),
        1200
      ); // Was 2500ms
      setTimeout(
        () => setElementsVisible((prev) => ({ ...prev, bubbleRight: true })),
        1400
      ); // Was 2800ms, staggered slightly
      setTimeout(
        () => setElementsVisible((prev) => ({ ...prev, bubbleBottom: true })),
        1600
      ); // Was 3100ms, staggered slightly

      setTimeout(() => {
        setAnimationStage("complete");
        if (onIntroAnimationComplete && !introCompletedRef.current) {
          onIntroAnimationComplete();
          introCompletedRef.current = true;
          // Re-enable scrolling when animation completes
          document.body.classList.remove("no-scroll");
        }
      }, 3500); // Adjusted to be after all (now longer) animations, was 2200ms
    } else if (runIntroAnimation === false) {
      // Animation is explicitly not to run (skipped or completed)
      // Set all elements to visible immediately and mark as complete
      setElementsVisible({
        logo: true,
        text: true,
        bubbleLeft: true,
        bubbleRight: true,
        bubbleBottom: true,
      });
      setAnimationStage("complete");
      if (onIntroAnimationComplete && !introCompletedRef.current) {
        onIntroAnimationComplete();
        introCompletedRef.current = true;
        // Re-enable scrolling when animation is skipped
        document.body.classList.remove("no-scroll");
      }
    }
    // If runIntroAnimation is null, do nothing, elements remain hidden from initial state

    // Cleanup function to ensure scrolling is re-enabled when component unmounts
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [runIntroAnimation, onIntroAnimationComplete]);

  // Determine the class for the main container based on animation stage.
  // If runIntroAnimation is null, stage remains 'start' (or initial) but elements aren't visible.
  const containerClass = runIntroAnimation === null ? "start" : animationStage;

  return (
    <div className={`home-intro-container ${containerClass}`}>
      <div
        className={`intro-logo-container ${
          elementsVisible.logo ? "visible" : ""
        }`}
      >
        <img
          src={process.env.PUBLIC_URL + "/EVEY LOGO.png"}
          alt="Evey Logo"
          className="intro-logo-image"
        />
      </div>

      <div
        className={`intro-text-container ${
          elementsVisible.text ? "visible" : ""
        }`}
      >
        <h1 className="intro-title">Welcome to Evey's World</h1>
        {/* Underline now handled by CSS ::after pseudo-element */}
      </div>

      <div className="nav-bubbles-container">
        <Link
          to="/about"
          className={`nav-bubble nav-bubble-left ${
            elementsVisible.bubbleLeft ? "visible" : ""
          }`}
          data-interactive="true"
        >
          <span>About Me</span>
        </Link>
        <Link
          to="/photography"
          className={`nav-bubble nav-bubble-right ${
            elementsVisible.bubbleRight ? "visible" : ""
          }`}
          data-interactive="true"
        >
          <span>Photography</span>
        </Link>
        <Link
          to="/media"
          className={`nav-bubble nav-bubble-bottom ${
            elementsVisible.bubbleBottom ? "visible" : ""
          }`}
          data-interactive="true"
        >
          <span>Media</span>
        </Link>
      </div>
    </div>
  );
};

export default Home;
